'use client';

import { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  Calendar,
  Users,
  Clock,
  Trophy,
  Gift,
  Radio,
  Award,
  ShoppingCart,
  SlidersHorizontal,
  X,
  ChevronRight,
  Lock,
} from 'lucide-react';
import { CatalogBannerCarousel } from '@/components/CatalogBannerCarousel';
import { Button, EmptyState, PAGE_CONTAINER } from '@/components/ui';
import type { LiveEventPublic, LiveEventCategory } from '@/lib/api';
import { useTranslations } from 'next-intl';
import { useNow } from '@/hooks/useNow';

const DASHBOARD_URL = process.env.NEXT_PUBLIC_DASHBOARD_URL || 'http://localhost:3502';


function formatShortDate(date: string) {
  return new Intl.DateTimeFormat('id-ID', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Jakarta',
  }).format(new Date(date));
}

function formatTime(date: string) {
  return new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta',
    timeZoneName: 'short',
  }).format(new Date(date));
}

function formatDuration(startAt: string, endAt: string) {
  const diff = new Date(endAt).getTime() - new Date(startAt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h} jam ${m} mnt` : `${h} jam`;
  }
  return `${mins} menit`;
}

function formatTimeRange(startAt: string, endAt: string, startMode: string) {
  const fmt = (d: string) => new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta',
  }).format(new Date(d));
  if (startMode === 'FLEXIBLE') {
    return `Fleksibel · ${fmt(startAt)}–${fmt(endAt)} WIB`;
  }
  const dur = formatDuration(startAt, endAt);
  return `${fmt(startAt)}–${fmt(endAt)} WIB · ${dur}`;
}

function StatusBadge({ status, size = 'sm' }: { status: string; size?: 'sm' | 'xs' }) {
  const t = useTranslations('events');
  const cls = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-[10px] px-1.5 py-0.5';
  switch (status) {
    case 'LIVE':
      return (
        <span className={`inline-flex items-center gap-1 font-bold text-white bg-red-500 rounded-full ${cls}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          LIVE
        </span>
      );
    case 'SCHEDULED':
    case 'REGISTRATION_CLOSED':
      return (
        <span className={`inline-flex items-center gap-1 font-semibold text-teal-700 bg-teal-50 rounded-full ${cls}`}>
          <Calendar className="w-3 h-3" />
          {t('mendatang')}
        </span>
      );
    case 'COMPLETED':
      return (
        <span className={`inline-flex items-center gap-1 font-semibold text-slate-500 bg-slate-100 rounded-full ${cls}`}>
          <Trophy className="w-3 h-3" />
          {t('selesai')}
        </span>
      );
    default:
      return null;
  }
}

function useCountdown(targetDate: string | null): string | null {
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    if (!targetDate) return;
    const compute = () => {
      const diff = new Date(targetDate).getTime() - Date.now();
      if (diff <= 0) { setLabel(null); return; }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      if (days > 0) setLabel(`${days} hari ${String(hours).padStart(2, '0')} jam`);
      else if (hours > 0) setLabel(`${hours} jam ${String(mins).padStart(2, '0')} mnt`);
      else setLabel(`${mins} menit`);
    };
    compute();
    const id = setInterval(compute, 30000);
    return () => clearInterval(id);
  }, [targetDate]);

  return label;
}

// Public CTA phase, derived from status AND dates — a stuck/lagging status (e.g. CALCULATING,
// or an event whose window already ended but isn't COMPLETED yet) must never show "Daftar".
// 'pending' is the pre-mount phase for the branches below that need the wall clock — see
// eventPhase's `now` param and useNow().
type EventPhase = 'completed' | 'live' | 'registerable' | 'ended' | 'closed' | 'cancelled' | 'pending';

function eventPhase(event: LiveEventPublic, now: number | null): EventPhase {
  if (event.status === 'COMPLETED') return 'completed';
  if (event.status === 'LIVE') return 'live';
  if (event.status === 'CANCELLED') return 'cancelled';
  // Remaining branches read the wall clock — stay undetermined until mounted.
  if (now === null) return 'pending';
  // Window ended (or being scored) → over. Not COMPLETED yet, so no leaderboard link.
  if (event.status === 'CALCULATING' || new Date(event.endAt).getTime() <= now) return 'ended';
  // Scheduled with registration still open → registerable.
  if (event.status === 'SCHEDULED' && new Date(event.registrationCloseAt).getTime() > now) return 'registerable';
  // REGISTRATION_CLOSED, or scheduled-but-registration-passed → can't join, not yet over.
  return 'closed';
}

/** Neutral non-interactive CTA label for events that are over / closed / cancelled. */
function inactivePhaseLabel(phase: EventPhase, t: (k: string) => string): string {
  if (phase === 'cancelled') return t('dibatalkan');
  if (phase === 'closed') return t('pendaftaranDitutup');
  return t('selesai'); // 'ended'
}

// --- Featured Event Card (hero-style, matches the page's white card pattern) ---

function FeaturedEventCard({ event, now }: { event: LiveEventPublic; now: number | null }) {
  const t = useTranslations('events');
  const phase = eventPhase(event, now);
  const isLive = phase === 'live';
  const isCompleted = phase === 'completed';
  const isUpcoming = phase === 'registerable';
  // listPublic passes `isPublished` straight through (see LiveEventPublic in lib/api.ts) —
  // false here means a private event the operator opted into the catalog via
  // isPubliclyListed. It's real and joinable, just gated by an access code, so the normal
  // "Daftar Gratis" / "Beli Tiket" CTA (which would hit register()'s EVENT_NOT_PUBLISHED)
  // must not render for it.
  const isInviteOnly = event.isPublished === false;
  const countdown = useCountdown(isUpcoming ? event.startAt : null);
  const accentColor = event.category.color ?? '#0f766e';
  const pricing = event.pricing;
  const hasDiscount = pricing != null && pricing.promoPrice != null && pricing.promoPrice < pricing.basePrice;
  const discountPct = hasDiscount
    ? Math.round(((pricing!.basePrice - pricing!.promoPrice!) / pricing!.basePrice) * 100)
    : 0;

  return (
    <div className="group relative bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-teal-300 hover:shadow-md transition-all">
      {/* Card-wide navigation. Overlaid rather than wrapping the card so the
          status CTAs below can stay real anchors without nesting <a> in <a>
          (which the HTML parser hoists out, breaking hydration). */}
      <Link href={`/events/${event.slug}`} className="absolute inset-0 z-10">
        <span className="sr-only">{event.title}</span>
      </Link>

      <div className="flex flex-col lg:flex-row">
        {/* Image */}
        <div className="relative lg:w-80 xl:w-96 shrink-0 aspect-video lg:aspect-auto bg-slate-100">
          {event.coverImageUrl ? (
            <Image
              src={event.coverImageUrl}
              alt={event.title}
              fill
              // contain, not cover: wide posters carry their title and get cropped mid-word otherwise
              className="object-contain group-hover:scale-105 transition-transform duration-300"
              sizes="(max-width: 1024px) 100vw, 384px"
            />
          ) : (
            <div
              className="w-full h-full min-h-[200px] flex items-center justify-center"
              style={{ background: `${accentColor}30` }}
            >
              <Radio className="w-12 h-12 opacity-20" style={{ color: accentColor }} />
            </div>
          )}
          {isLive && (
            <div className="absolute top-3 left-3">
              <StatusBadge status="LIVE" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5 lg:p-6 flex-1 min-w-0 flex flex-col">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span
              className="text-[10px] font-semibold px-2 py-0.5 rounded-md"
              style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
            >
              {event.category.name}
            </span>
            {!isLive && <StatusBadge status={event.status} size="xs" />}
            {isInviteOnly && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-violet-50 text-violet-600">
                <Lock className="w-2.5 h-2.5" /> {t('undangan')}
              </span>
            )}
            {event.isFree ? (
              <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-md">
                {t('gratisBadge')}
              </span>
            ) : (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-600">
                {t('berbayar')}
              </span>
            )}
            {hasDiscount && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-red-50 text-red-500">
                -{discountPct}%
              </span>
            )}
          </div>

          <h2 className="text-lg font-bold text-slate-900 mb-1 line-clamp-2 group-hover:text-teal-700 transition-colors">{event.title}</h2>

          {event.description && (
            <p className="text-sm text-slate-500 leading-relaxed mb-3 line-clamp-2">{event.description}</p>
          )}

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mb-4">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-slate-400" />
              {formatShortDate(event.startAt)} · {formatTime(event.startAt)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-slate-400" />
              {formatTimeRange(event.startAt, event.endAt, event.startMode)}
            </span>
            {event.maxParticipants && (
              <span className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5 text-slate-400" />
                {t('maks', { count: event.maxParticipants })}
              </span>
            )}
          </div>

          {event.prizes && event.prizes.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {event.prizes.slice(0, 3).map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded-md">
                  <Award className="w-3 h-3" />
                  #{p.minRank === p.maxRank ? p.minRank : `${p.minRank}-${p.maxRank}`}: {p.description}
                </span>
              ))}
            </div>
          )}

          {/* Countdown — only for upcoming events */}
          {isUpcoming && countdown && (
            <div className="flex items-center gap-1.5 mb-3 text-sm font-semibold text-teal-700 bg-teal-50 px-3 py-1.5 rounded-lg self-start">
              <Clock className="w-4 h-4" />
              {t('mulaiDalam', { countdown })}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 mt-auto pt-3 border-t border-slate-100">
            {/* Price */}
            <div className="shrink-0">
              {event.isFree ? (
                <span className="font-bold text-lg text-emerald-600">{t('gratis')}</span>
              ) : hasDiscount ? (
                <span className="flex items-baseline gap-1.5">
                  <span className="font-bold text-lg text-orange-600">Rp&nbsp;{pricing!.promoPrice!.toLocaleString('id-ID')}</span>
                  <span className="text-xs text-slate-400 line-through">Rp&nbsp;{pricing!.basePrice.toLocaleString('id-ID')}</span>
                </span>
              ) : pricing ? (
                <span className="font-bold text-lg text-slate-900">Rp&nbsp;{pricing.basePrice.toLocaleString('id-ID')}</span>
              ) : (
                <span className="text-xs text-slate-400 italic">{t('hargaBelumDitetapkan')}</span>
              )}
            </div>

            {/* CTA */}
            {isCompleted ? (
              <Link
                href={`/events/${event.slug}/leaderboard`}
                className="relative z-20 inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-lg transition-colors"
              >
                <Trophy className="w-3.5 h-3.5" />
                {t('lihatLeaderboard')}
              </Link>
            ) : isLive ? (
              <a
                href={`${DASHBOARD_URL}/events/${event.slug}`}
                className="relative z-20 inline-flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <Radio className="w-3.5 h-3.5" /> {t('mulaiSekarang')}
              </a>
            ) : isInviteOnly ? (
              // No register/cart CTA for a listed-but-private event — the card-wide overlay
              // <Link> still navigates to the detail page, where the actual invite-code CTA
              // lives (see EventDetailClient's 'requires_code' variant).
              <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-violet-50 text-violet-600 text-sm font-semibold rounded-lg cursor-default">
                <Lock className="w-3.5 h-3.5" /> {t('undangan')}
              </span>
            ) : phase === 'pending' ? (
              // Wall-clock phase not resolved yet (pre-mount) — neutral loading pill instead
              // of guessing a label that might flip right after hydration.
              <span className="inline-flex items-center px-4 py-2 rounded-lg">
                <span className="w-20 h-4 bg-slate-100 rounded animate-pulse" />
              </span>
            ) : phase !== 'registerable' ? (
              <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-slate-500 text-sm font-semibold rounded-lg cursor-default">
                {inactivePhaseLabel(phase, t)}
              </span>
            ) : (
              // Non-interactive by choice: the card-wide overlay <Link> already
              // navigates to the event detail page (same destination).
              <span
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                  event.isFree
                    ? 'bg-teal-600 hover:bg-teal-700 text-white'
                    : 'bg-orange-500 hover:bg-orange-600 text-white'
                }`}
              >
                <ShoppingCart className="w-3.5 h-3.5" />
                {event.isFree ? t('daftarGratis') : t('beliTiket')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Event Card (matches ModuleCard / CourseCard style) ---

function EventCard({ event, now }: { event: LiveEventPublic; now: number | null }) {
  const t = useTranslations('events');
  const phase = eventPhase(event, now);
  const isLive = phase === 'live';
  const isCompleted = phase === 'completed';
  // See the matching comment in FeaturedEventCard — isPublished:false + still listed means
  // access-code-gated, not open registration.
  const isInviteOnly = event.isPublished === false;
  const accentColor = event.category.color ?? '#0f766e';
  const pricing = event.pricing;
  const hasDiscount = pricing != null && pricing.promoPrice != null && pricing.promoPrice < pricing.basePrice;
  const discountPct = hasDiscount
    ? Math.round(((pricing!.basePrice - pricing!.promoPrice!) / pricing!.basePrice) * 100)
    : 0;

  return (
    <div className="group relative flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-teal-300 hover:shadow-md transition-all">
      {/* Real anchor rather than an onClick handler: keyboard-focusable, gives
          crawlers an href, and supports middle-click / open-in-new-tab. */}
      <Link href={`/events/${event.slug}`} className="absolute inset-0 z-10">
        <span className="sr-only">{event.title}</span>
      </Link>

      {/* Thumbnail */}
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
        {event.coverImageUrl ? (
          <Image
            src={event.coverImageUrl}
            alt={event.title}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-300"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: `${accentColor}30` }}
          >
            <Radio className="w-8 h-8 opacity-20" style={{ color: accentColor }} />
          </div>
        )}
      </div>

      <div className="p-3 flex flex-col flex-1">
        {/* Badges row */}
        <div className="flex items-center gap-1 flex-wrap mb-2">
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
          >
            {event.category.name}
          </span>
          <StatusBadge status={event.status} size="xs" />
          {isInviteOnly && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-50 text-violet-600">
              <Lock className="w-2.5 h-2.5" /> {t('undangan')}
            </span>
          )}
          {event.isFree ? (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600">
              {t('gratis')}
            </span>
          ) : (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">
              {t('berbayar')}
            </span>
          )}
          {hasDiscount && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-500">
              -{discountPct}%
            </span>
          )}
        </div>

        <h3 className="font-bold text-slate-900 text-sm leading-snug mb-2.5 line-clamp-2 group-hover:text-teal-700 transition-colors">
          {event.title}
        </h3>

        {/* Meta */}
        <div className="space-y-1 text-sm text-slate-400 mb-3">
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {formatShortDate(event.startAt)}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            {formatTimeRange(event.startAt, event.endAt, event.startMode)}
            {event.maxParticipants && (
              <span className="ml-1">· {t('maksShort', { count: event.maxParticipants })}</span>
            )}
          </span>
        </div>

        {/* Price footer */}
        <div className="pt-3 border-t border-slate-100 mt-auto flex items-center justify-between">
          {event.isFree ? (
            <span className="font-bold text-base text-emerald-600">{t('gratis')}</span>
          ) : hasDiscount ? (
            <span className="flex items-baseline gap-1.5 flex-wrap">
              <span className="font-bold text-base text-orange-600">
                Rp&nbsp;{pricing!.promoPrice!.toLocaleString('id-ID')}
              </span>
              <span className="text-xs text-slate-400 line-through">
                Rp&nbsp;{pricing!.basePrice.toLocaleString('id-ID')}
              </span>
            </span>
          ) : pricing ? (
            <span className="font-bold text-base text-slate-900">
              Rp&nbsp;{pricing.basePrice.toLocaleString('id-ID')}
            </span>
          ) : (
            <span className="text-xs text-slate-400 italic">{t('hargaBelumDitetapkan')}</span>
          )}
          {/* CTA */}
          {isCompleted ? (
            <Link
              href={`/events/${event.slug}/leaderboard`}
              className="relative z-20 flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
            >
              <Trophy className="w-3.5 h-3.5" /> {t('lihatLeaderboard')}
            </Link>
          ) : isLive ? (
            <a
              href={`${DASHBOARD_URL}/events/${event.slug}`}
              className="relative z-20 flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
            >
              <Radio className="w-3.5 h-3.5" /> {t('mulai')}
            </a>
          ) : isInviteOnly ? (
            // No register/cart CTA for a listed-but-private event — see the matching branch
            // in FeaturedEventCard.
            <span className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-violet-50 text-violet-600 cursor-default">
              <Lock className="w-3.5 h-3.5" /> {t('undangan')}
            </span>
          ) : phase === 'pending' ? (
            // Wall-clock phase not resolved yet (pre-mount) — neutral loading pill instead
            // of guessing a label that might flip right after hydration.
            <span className="flex items-center px-2.5 py-1.5 rounded-lg">
              <span className="w-14 h-3.5 bg-slate-100 rounded animate-pulse" />
            </span>
          ) : phase !== 'registerable' ? (
            <span className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-500 cursor-default">
              {inactivePhaseLabel(phase, t)}
            </span>
          ) : (
            // Non-interactive by choice: the card-wide overlay <Link> already
            // navigates to the event detail page, where registration happens.
            <span
              className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors ${
                event.isFree
                  ? 'text-teal-600 bg-teal-50 hover:bg-teal-100'
                  : 'text-orange-600 bg-orange-50 hover:bg-orange-100'
              }`}
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              {event.isFree ? t('daftar') : t('beliTiket')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Status Tab Bar ---

type TabFilter = 'all' | 'upcoming' | 'live' | 'completed';

// --- Main Component ---

export function EventsHubClient({
  initialEvents,
  categories,
  archiveMode = false,
}: {
  initialEvents: LiveEventPublic[];
  categories: LiveEventCategory[];
  /** Past-events archive variant: starts on the Completed tab, hides the featured hero. */
  archiveMode?: boolean;
}) {
  const t = useTranslations('events');
  const now = useNow();
  const [activeTab, setActiveTab] = useState<TabFilter>(archiveMode ? 'completed' : 'all');
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [pricingFilter, setPricingFilter] = useState<'all' | 'free' | 'paid'>('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const hasFilters = activeTab !== 'all' || activeCategoryId !== null || pricingFilter !== 'all';

  // A finished event is never the hero: the past-events entry and the list cover it.
  const featured = useMemo(() => {
    const live = initialEvents.find(e => e.status === 'LIVE');
    if (live) return live;
    const upcoming = initialEvents
      .filter(e => ['SCHEDULED', 'REGISTRATION_CLOSED'].includes(e.status))
      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    return upcoming[0] ?? null;
  }, [initialEvents]);

  const filteredEvents = useMemo(() => {
    let events = initialEvents;

    if (activeTab === 'upcoming') {
      events = events.filter(e => ['SCHEDULED', 'REGISTRATION_CLOSED'].includes(e.status));
    } else if (activeTab === 'live') {
      events = events.filter(e => e.status === 'LIVE');
    } else if (activeTab === 'completed') {
      events = events.filter(e => e.status === 'COMPLETED');
    }

    if (activeCategoryId) {
      events = events.filter(e => e.category.id === activeCategoryId);
    }

    if (pricingFilter === 'free') events = events.filter(e => e.isFree);
    else if (pricingFilter === 'paid') events = events.filter(e => !e.isFree);

    // Sort: LIVE first, then upcoming (by start date asc), then completed last (by start date desc)
    const statusOrder: Record<string, number> = { LIVE: 0, SCHEDULED: 1, REGISTRATION_CLOSED: 1, COMPLETED: 2 };
    events = [...events].sort((a, b) => {
      const orderDiff = (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9);
      if (orderDiff !== 0) return orderDiff;
      // Within same status group: upcoming sort by soonest first, completed sort by most recent first
      if (a.status === 'COMPLETED') return new Date(b.startAt).getTime() - new Date(a.startAt).getTime();
      return new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
    });

    return events;
  }, [initialEvents, activeTab, activeCategoryId, pricingFilter]);

  const liveCount = initialEvents.filter(e => e.status === 'LIVE').length;
  const upcomingCount = initialEvents.filter(e => ['SCHEDULED', 'REGISTRATION_CLOSED'].includes(e.status)).length;
  const completedCount = initialEvents.filter(e => e.status === 'COMPLETED').length;

  const tabs: { key: TabFilter; label: string; count: number }[] = [
    { key: 'all', label: t('semua'), count: initialEvents.length },
    { key: 'live', label: t('sedangLiveTab'), count: liveCount },
    { key: 'upcoming', label: t('mendatang'), count: upcomingCount },
    { key: 'completed', label: t('selesai'), count: completedCount },
  ];

  const activeLabel = activeCategoryId
    ? categories.find(c => c.id === activeCategoryId)?.name
    : null;

  return (
    <div className="pb-24">
      {/* Page header. Visible at every width so the h1 never disappears on phones. */}
      <div className="bg-pattern bg-teal-700 py-8 sm:py-10 text-white">
        <div className={PAGE_CONTAINER}>
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight mb-1">
            {archiveMode ? t('pastEventsTitle') : t('title')}
          </h1>
          <p className="text-teal-50 text-sm">
            {archiveMode ? t('pastEventsDesc') : t('countDesc', { count: initialEvents.length })}
            {!archiveMode && activeLabel && <span className="font-semibold text-white"> · {activeLabel}</span>}
            {!archiveMode && liveCount > 0 && <span className="font-semibold text-white"> · {t('sedangLive', { count: liveCount })}</span>}
          </p>
        </div>
      </div>

      {/* Banner — full-width, flush to edges */}
      <div className="mt-3 sm:mt-6 sm:max-w-[1280px] sm:mx-auto sm:px-6 lg:px-8">
        <CatalogBannerCarousel />
      </div>

      <div className={`${PAGE_CONTAINER} pt-4 sm:pt-0`}>
        {/* Featured event (hidden in the past-events archive) */}
        {featured && !archiveMode && (
          <div className="mb-6">
            <FeaturedEventCard event={featured} now={now} />
          </div>
        )}

        {/* No live/upcoming events → point visitors to the past-events archive so finished
            events + their leaderboards stay discoverable. */}
        {!archiveMode && liveCount === 0 && upcomingCount === 0 && completedCount > 0 && (
          <Link
            href="/events/sebelumnya"
            className="group mb-6 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 transition-colors hover:border-teal-300 hover:bg-teal-50/40"
          >
            <span className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-600">
                <Trophy className="h-4 w-4" />
              </span>
              <span>
                <span className="block text-sm font-bold text-slate-900">{t('pastEventsCta')}</span>
                <span className="block text-xs text-slate-500">{t('pastEventsCtaSub', { count: completedCount })}</span>
              </span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:text-teal-600" />
          </Link>
        )}

        {/* Mobile: single filter row + collapsible drawer */}
        <div className="lg:hidden mb-4">
          <div className="flex overflow-x-auto gap-2 pb-3 -mx-4 px-4 [&::-webkit-scrollbar]:hidden">
            {/* Filter button */}
            <button
              onClick={() => setShowMobileFilters(v => !v)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                showMobileFilters ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-700 border-slate-300'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              {t('filterTitle')}
              {hasFilters && <span className="w-3.5 h-3.5 bg-teal-500 text-white rounded-full text-[9px] flex items-center justify-center">!</span>}
            </button>
            {/* Category chips */}
            <button
              onClick={() => setActiveCategoryId(null)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${!activeCategoryId ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200'}`}
            >
              {t('semua')}
            </button>
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategoryId(activeCategoryId === cat.id ? null : cat.id)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${activeCategoryId === cat.id ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200'}`}
              >
                {cat.name}
              </button>
            ))}
            <div className="w-px h-5 bg-slate-200 self-center flex-shrink-0" />
            <button onClick={() => setPricingFilter(pricingFilter === 'free' ? 'all' : 'free')} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${pricingFilter === 'free' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200'}`}>{t('gratis')}</button>
            <button onClick={() => setPricingFilter(pricingFilter === 'paid' ? 'all' : 'paid')} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border whitespace-nowrap transition-colors ${pricingFilter === 'paid' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-slate-600 border-slate-200'}`}>{t('berbayar')}</button>
          </div>

          {/* Collapsible filter drawer */}
          {showMobileFilters && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-slate-500" />
                  <span className="font-semibold text-slate-900 text-sm">{t('filterTitle')}</span>
                </div>
                <div className="flex items-center gap-3">
                  {hasFilters && (
                    <button
                      onClick={() => { setActiveTab('all'); setActiveCategoryId(null); setPricingFilter('all'); }}
                      className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> {t('reset')}
                    </button>
                  )}
                  <button onClick={() => setShowMobileFilters(false)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Status */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{t('status')}</p>
                <div className="flex flex-col gap-1">
                  {tabs.map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between ${
                        activeTab === tab.key ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {tab.label}
                      <span className="text-xs text-slate-400">{tab.count}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Harga */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{t('harga')}</p>
                <div className="flex flex-col gap-1">
                  {([['all', t('semua')], ['free', t('gratis')], ['paid', t('berbayar')]] as const).map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setPricingFilter(val)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${pricingFilter === val ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Categories */}
              {categories.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{t('kategori')}</p>
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => setActiveCategoryId(null)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!activeCategoryId ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                      {t('semuaKategori')}
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setActiveCategoryId(activeCategoryId === cat.id ? null : cat.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${activeCategoryId === cat.id ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-slate-600 hover:bg-slate-50'}`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Main layout */}
        <div className="flex gap-6">
          {/* Left sidebar — desktop */}
          <div className="w-60 shrink-0 hidden lg:block">
            <div className="sticky top-[88px] space-y-5">
              {/* Status tabs (vertical on desktop) */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{t('status')}</p>
                <div className="space-y-0.5">
                  {tabs.map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeTab === tab.key
                          ? 'bg-teal-50 text-teal-700 font-medium'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {tab.label}
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        activeTab === tab.key ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-400'
                      }`}>
                        {tab.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Harga filter */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{t('harga')}</p>
                <div className="space-y-0.5">
                  {([['all', t('semua')], ['free', t('gratis')], ['paid', t('berbayar')]] as const).map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setPricingFilter(val)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        pricingFilter === val ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category filter */}
              {categories.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{t('kategori')}</p>
                  <div className="space-y-0.5">
                    <button
                      onClick={() => setActiveCategoryId(null)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        !activeCategoryId ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {t('semuaKategori')}
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setActiveCategoryId(activeCategoryId === cat.id ? null : cat.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          activeCategoryId === cat.id ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main grid */}
          <div className="flex-1 min-w-0">
            {/* Results count */}
            <p className="text-sm text-slate-400 mb-3">
              {t('menampilkan', { count: filteredEvents.length, total: initialEvents.length })}
            </p>

            {filteredEvents.length === 0 ? (
              <EmptyState
                icon={<Radio className="w-12 h-12" />}
                title={t('belumAdaEvent')}
                description={t('ubahFilter')}
              />
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                {filteredEvents.map(event => (
                  <EventCard key={event.id} event={event} now={now} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* CTA section — matches tests/courses bottom CTA banner */}
        <div className="bg-pattern overflow-hidden mt-12 bg-teal-700 rounded-xl p-5 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-lg">{t('ctaTitle')}</h3>
              <p className="text-teal-50 text-sm">{t('ctaDesc')}</p>
            </div>
            <Button href={`${DASHBOARD_URL}/register`} variant="primary">
              <Gift className="w-4 h-4" />
              {t('daftarGratisCta')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
